import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setTheme, updateResolvedTheme } from '../../store/slices/themeSlice';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { theme, resolvedTheme } = useAppSelector((state) => state.theme);

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    // Remove both theme classes first
    root.classList.remove('theme-light', 'theme-dark');
    // Add the appropriate theme class
    root.classList.add(`theme-${resolvedTheme}`);
    
    // Also set a data attribute for CSS targeting
    root.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    // Listen for system theme changes
    if (theme === 'system' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        dispatch(updateResolvedTheme());
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, [theme, dispatch]);

  return <>{children}</>;
};

export default ThemeProvider;
